import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Header } from "../../../components/header";
import { Loading } from "../../../components/loading";
import { CardHeader } from "../../../components/cards";
import { SendIcon } from "../../../components/icons";
import { useTask, useRemoveTask } from "../hooks";
import { TaskPriorityBadge } from "../components";
import { useMe } from "../../users/hooks/useMe";
import { UserRole } from "../../../__generated__/globalTypes";
import { formatDisplayDate } from "../../../helpers/date";
import { taskStatusLabel } from "../../../helpers/enum-labels";

type ITaskParams = {
  id: string;
};

export const Task: React.FC = () => {
  const { id } = useParams<ITaskParams>();
  const navigate = useNavigate();
  const [removeTask] = useRemoveTask();
  const { data: meData } = useMe();
  const isAdmin = meData?.me?.role === UserRole.Admin;

  useEffect(() => {
    if (!id) {
      navigate("/");
    }
  }, []);

  const { data, loading, error } = useTask(+id!);

  if (loading) return <Loading />;

  if (error || data?.task?.ok === false) {
    return (
      <div className="main-container">
        <div className="card">
          <p className="text-red-600">
            {data?.task?.error ?? "Vous n'avez pas accès à cette tâche"}
          </p>
        </div>
      </div>
    );
  }

  const task = data?.task?.result;

  const handleRemove = async () => {
    if (!window.confirm("Supprimer cette tâche ? Cette action est irréversible.")) {
      return;
    }
    await removeTask({ variables: { id: +id! } });
    toast.success("Tâche supprimée");
    navigate("/tasks");
  };

  return (
    <>
      <Header
        title="Tâche"
        subtitle={task?.title}
        buttons={[
          {
            actionText: "Modifier",
            bgColor: "indigo",
            textColor: "white",
            link: `/task/${id}/update`,
            icon: <SendIcon />,
          },
        ]}
      />
      <div className="main-container">
        <div className="card">
          <CardHeader title="Informations générales" />
          <div className="w-full mt-3">
            <p className="label">Titre</p>
            <input className="input w-full" value={task?.title} disabled />
          </div>
          <div className="w-full mt-3">
            <p className="label">Description</p>
            <textarea className="input w-full" value={task?.description} disabled rows={3} />
          </div>
          <div className="flex mt-3 items-center gap-4">
            <div>
              <p className="label">Statut</p>
              <input className="input" value={taskStatusLabel(task?.status)} disabled />
            </div>
            <div>
              <p className="label">Priorité</p>
              {task?.priority && <TaskPriorityBadge priority={task.priority} />}
            </div>
          </div>
          <div className="w-full mt-3">
            <p className="label">Échéance</p>
            <input
              className="input w-full"
              value={task?.dueDate ? formatDisplayDate(task.dueDate) : "Pas d'échéance"}
              disabled
            />
          </div>
          {task?.user && (
            <div className="w-full mt-3">
              <p className="label">Propriétaire</p>
              <input
                className="input w-full"
                value={`${task.user.firstname} ${task.user.lastname}`}
                disabled
              />
            </div>
          )}

          {isAdmin && (
            <div className="w-full mt-4">
              <span className="text-red-500 cursor-pointer text-sm" onClick={handleRemove}>
                Supprimer cette tâche
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
